import { MEDIA_QUERY } from '@sus-core/hooks/useMediaQuery';

import { Machine } from 'xstate/lib/Machine';
import { interpret } from 'xstate/lib/interpreter';
import { ActorRefFrom } from 'xstate/lib/types';
import { Aggregations, filterMachine } from '../filter/filter.machine';

export type FilterActor = ActorRefFrom<typeof filterMachine>;

export interface AppContext {
  query?: string;
  filters: Record<string, FilterActor>;
}

interface AppStateSchema {
  states: {
    desktop: any;
    mobile: any;
  };
}

interface SwitchMobileEvent {
  type: 'SWITCH_MOBILE';
}

interface SwitchDesktopeEvent {
  type: 'SWITCH_DESKTOP';
}

interface OpenCartEvent {
  type: 'CART_OPEN';
}

interface CloseCartEvent {
  type: 'CART_CLOSE';
}

interface OpenMenuEvent {
  type: 'MENU_OPEN';
}

interface CloseMenuEvent {
  type: 'MENU_CLOSE';
}

/// search related events
interface OpenSearchEvent {
  type: 'SEARCH_OPEN';
}

interface CloseSearchEvent {
  type: 'SEARCH_CLOSE';
}

///

interface FilterInitEvent {
  type: 'FILTER_INIT';
  key: string;
  aggregations: Aggregations;
  productPrices: ProductPrice[];
}

type ResetFilterEvent = { type: 'FILTER_RESET'; id: string };

type AppEvent =
  | SwitchMobileEvent
  | SwitchDesktopeEvent
  | OpenCartEvent
  | CloseCartEvent
  | OpenMenuEvent
  | CloseMenuEvent
  | OpenSearchEvent
  | CloseSearchEvent
  | FilterInitEvent
  | ResetFilterEvent;

const cartStates = {
  initial: 'closed',
  states: {
    open: {
      on: {
        CART_CLOSE: 'closed'
      }
    },
    closed: {
      on: {
        CART_OPEN: 'open'
      }
    }
  }
};

const searchStates = {
  initial: 'closed',
  states: {
    open: {
      on: {
        SEARCH_CLOSE: 'closed'
      }
    },
    closed: {
      on: {
        SEARCH_OPEN: 'open'
      }
    }
  }
};

const appMachine = Machine<AppContext, AppStateSchema, AppEvent>(
  {
    id: 'app',
    initial: 'desktop',
    context: {
      filters: {}
    },
    on: {
      SWITCH_MOBILE: 'mobile',
      SWITCH_DESKTOP: 'desktop'
    },
    states: {
      mobile: {
        type: 'parallel',
        states: {
          menu: {
            initial: 'closed',
            states: {
              open: {
                on: {
                  MENU_CLOSE: { target: 'closed' }
                }
              },
              closed: {
                on: {
                  MENU_OPEN: { target: 'open' }
                }
              }
            }
          },
          sidecart: {
            ...cartStates
          },
          search: {
            ...searchStates
          }
        }
      },
      desktop: {
        type: 'parallel',
        states: {
          sidecart: {
            ...cartStates
          },
          search: {
            ...searchStates
          }
        }
      }
    }
  },
  {}
);

export const appService = interpret(appMachine).start();

export function init() {
  if (typeof window !== 'undefined') {
    const media = window.matchMedia(MEDIA_QUERY.IS_MOBILE);
    appService.send({
      type: media.matches ? 'SWITCH_MOBILE' : 'SWITCH_DESKTOP'
    });

    media.addEventListener('change', event => {
      appService.send({
        type: event.matches ? 'SWITCH_MOBILE' : 'SWITCH_DESKTOP'
      });
    });

    // window.dispatchEvent(new Event('client-entry'));
  }
}

export function openMenu() {
  appService.send({
    type: 'MENU_OPEN'
  });
}

export function closeMenu() {
  appService.send({
    type: 'MENU_CLOSE'
  });
}

export function openCart() {
  appService.send({
    type: 'CART_OPEN'
  });
}

export function closeCart() {
  appService.send({
    type: 'CART_CLOSE'
  });
}

export function openSearch() {
  appService.send({
    type: 'SEARCH_OPEN'
  });
}

export function closeSearch() {
  appService.send({
    type: 'SEARCH_CLOSE'
  });
}

export type ProductPrice = Pick<
  Queries.SUS_ProductInterface,
  'price_range' | 'sku'
>;
export function getFilters(
  key: string | number,
  aggregations: Aggregations,
  productPrices: ProductPrice[]
) {
  appService.send({
    type: 'FILTER_INIT',
    aggregations,
    productPrices,
    key: key + ''
  });

  return appService.getSnapshot().context.filters[key];
}
